<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell
    :title="$t('views.selectedGoat.modules.famacha')"
    :icon="Eye"
    @click="goToFamachaView"
    class="cursor-pointer"
  >
    <div v-if="famachaStore.loading" class="text-sm opacity-50 text-center">
      {{ $t('views.selectedGoat.loading') }}
    </div>

    <!-- Display current famacha if available -->
    <div v-else-if="latestFamacha" class="flex items-center justify-between">
      <div>
        <p class="text-sm text-[var(--color-text)] opacity-70">
          {{ $t('views.selectedGoat.famacha.current') }}
        </p>
        <div
          class="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl border-2 border-gray-300 !text-gray-900"
          :style="getScoreBgStyle(latestFamacha.score)"
        >
          {{ latestFamacha.score }}
        </div>

      </div>
      <div class="text-right">
        <p class="text-xs text-[var(--color-text)] opacity-50">
          {{ formatDate(latestFamacha.measuredAt) }}
        </p>
      </div>
    </div>

    <!-- No famacha data -->
    <div v-else class="text-sm opacity-70 text-center py-2">
      {{ $t('views.selectedGoat.famacha.noData') }}
    </div>
  </ModuleShell>
</template>


<script setup>
import { onMounted, computed } from 'vue'
import { useFamachaStore } from '@/stores/famacha.store.js'
import { storeToRefs } from 'pinia'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import { useRouter } from 'vue-router'
import { Eye } from 'lucide-vue-next'
import { formatDate } from '@/utils/format/dateformatting'

const props = defineProps({
  goatId: { type: Number, required: true }
})

const router = useRouter()
const famachaStore = useFamachaStore()
const { famachaChecks } = storeToRefs(famachaStore)

// Get the latest famacha (most recent)
const latestFamacha = computed(() => {
  if (!famachaChecks.value || famachaChecks.value.length === 0) return null
  return famachaChecks.value[0]
})

function getScoreBgStyle(score) {
  const styles = {
    1: 'background-color: #f94943',
    2: 'background-color: #ff7963',
    3: 'background-color: #faa89e',
    4: 'background-color: #fedcca',
    5: 'background-color: #ffffff'
  }
  return styles[score] || 'background-color: #6b7280'
}


function goToFamachaView() {
  if (!props.goatId) return
  router.push({ name: 'Famacha', params: { id: props.goatId } })
}

// Fetch famacha if mounted
onMounted(() => {
  if (props.goatId) famachaStore.fetchFamachaChecks(props.goatId)
})
</script>

<style scoped>
</style>
